// src/components/Question/Question.jsx
"use client"

import React from "react";
import katex from "katex";
import "katex/dist/katex.min.css";
import "katex/dist/contrib/mhchem";
import { motion } from "framer-motion";
import "../../styles/theme-components.css";

const detectLanguage = (text) => {
  if (!text) return "en";

  const cleanText = text
    .replace(/\\[a-zA-Z]+(?:{[^{}]*})?/g, "") 
    .replace(/[\$\[\]\{\}\\]/g, "") 
    .replace(/[0-9.,;:!?()<>|\\/\-_=+*&^%$#@~`"']/g, "") 
    .trim();

  if (!cleanText) return "en"; 

  const words = cleanText.split(/\s+/).filter(word => word.length > 1);

  if (words.length === 0) return "en"; 

  let persianWords = 0;
  let englishWords = 0;

  const persianRegex = /^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF]+$/;
  const englishRegex = /^[a-zA-Z]+$/;

  words.forEach(word => {
    if (persianRegex.test(word)) {
      persianWords++;
    } else if (englishRegex.test(word)) {
      englishWords++;
    }
  });

  const totalWords = persianWords + englishWords;
  if (totalWords === 0) return "en"; 

  const persianRatio = persianWords / totalWords;

  return persianWords > 0 && (persianRatio >= 0.5 || words.length <= 3) ? "fa" : "en";
};

const renderMath = (text, latex) => {
  let processedText = text || "";
  if (latex) {
    const rawParts = latex.split(",").map((p) => p.trim());
    const parts = rawParts.filter((p) => p && p !== "ce{}");

    const renderedParts = parts.map((part, idx) => {
      try {
        let html;
        if (part.startsWith("\\ce{") || part.startsWith("ce{")) {
          const chem = part.replace(/^\\?ce\{(.*)\}$/, "$1").trim();
          html = katex.renderToString(`\\ce{${chem}}`, { throwOnError: false });
        } else {
          const hasDelims = /\\\[.*?\\\]|\\$$.*?\\$$|\$\$.*?\$\$|\$.*?\$/.test(part);
          let expr;
          if (hasDelims) {
            expr = part.replace(/\\\[(.*?)\\\]|\\$$(.*?)\\$$|\$\$(.*?)\$\$|\$(.*?)\$/, "$1$2$3$4");
          } else {
            expr = part;
          }
          console.log(`Rendering math formula [${idx}]:`, expr);
          html = katex.renderToString(expr, {
            throwOnError: false,
            displayMode: /\\\[.*?\\\]|\$\$.*?\$\$/.test(part),
          });
        }
        return html;
      } catch (err) {
        return '<span style="color: rgb(var(--error-color));">[Invalid LaTeX]</span>';
      }
    });

    const latexHTML = `<span class="katex">${renderedParts.join(" ")}</span>`;
    processedText = processedText ? `${processedText}<br/>${latexHTML}` : latexHTML;
  }

  const output = processedText || '<span style="color: rgb(var(--text-secondary));">[No content]</span>';
  return output;
};


const Question = ({ question, image, options, onChange, selectedOptions = [] }) => {
  const questionText = renderMath(question.text, question.latex);
  const questionLanguage = detectLanguage(question.text || "");
  const isRtl = questionLanguage === "fa";

  return (
    <div
      className="question-card rounded-xl overflow-hidden bg-[rgb(var(--card-bg))] border-[rgb(var(--card-border))] shadow-lg transition-all duration-300 hover:shadow-xl max-h-[90vh]"
      dir="ltr"
    >
      <div className="p-6 border-b border-[rgb(var(--card-border))]">
        <h2
          className="text-xl font-semibold text-[rgb(var(--text-primary))] line-clamp-3 text-left"
          dangerouslySetInnerHTML={{ __html: questionText }}
          aria-label={`سوال: ${question.text || "بدون متن"}`}
        />
      </div>

      <div className="md:flex flex-row">
        {image && (
          <div className="md:w-2/5 p-4">
            <div className="rounded-lg overflow-hidden shadow-md max-h-[400px] md:max-h-[300px] aspect-[4/3]">
              <img
                src={image || "/placeholder.svg"}
                alt="Illustration for question"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        )}

        <div className={`p-6 ${image ? "md:w-3/5" : "w-full"} max-h-[calc(90vh-150px)] overflow-y-auto`}>
          <div className="space-y-4">
            {options.map((option, index) => {
              const isSelected = selectedOptions.includes(option.id);
              const optionText = renderMath(option.text, option.latex);
              const optionLanguage = detectLanguage(option.text || "");
              const isOptionRtl = optionLanguage === "fa";

              return (
                <motion.div
                  key={option.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <label
                    className={`flex items-start p-4 rounded-lg cursor-pointer transition-all duration-200 
                      ${
                        isSelected
                          ? "bg-[rgb(var(--question-selected-bg))] border-2 border-[rgb(var(--question-selected-border))]"
                          : "bg-[rgb(var(--form-bg))] border-2 border-transparent hover:border-[rgb(var(--question-hover-bg))]"
                      }`}
                    dir="ltr"
                  >
                    <div className="flex-shrink-0 mt-0.5 relative">
                      <input
                        type="checkbox"
                        name={`question-${question.id}`}
                        value={option.id}
                        className={`h-5 w-5 rounded border-2 transition-colors duration-200 
                          ${
                            isSelected
                              ? "border-[rgb(var(--checkbox-checked))] bg-[rgb(var(--checkbox-checked))]"
                              : "border-[rgb(var(--checkbox-border))] bg-[rgb(var(--checkbox-bg))]"
                          }`}
                        checked={isSelected}
                        onChange={() => onChange(option.id)}
                        aria-label={`گزینه ${index + 1}: ${option.text || "بدون متن"}`}
                      />
                    </div>

                    <div className="ml-4 flex-1">
                      <div className="flex items-center">
                        <span
                          className="inline-flex items-center justify-center h-6 w-6 rounded-full 
                            bg-[rgb(var(--question-selected-bg))] text-[rgb(var(--text-primary))] text-sm font-medium 
                            mr-2"
                        >
                          {String.fromCharCode(97 + index)}
                        </span>
                        <span
                          className="text-[rgb(var(--text-primary))] text-base line-clamp-2 text-left"
                          dangerouslySetInnerHTML={{ __html: optionText }}
                        />
                      </div>
                    </div>
                  </label>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(Question);